'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, ChainId, MarketId } from '@morpho-org/blue-sdk';
import { toBigInt } from '@morpho-org/dapp-core';
import { cn, formatters, getDigitsFromPrice } from '@morpho-org/ui-v6/utils';
import React, { memo, useMemo } from 'react';
import { ProtectedLltv, ProtectedLltvLabel, ProtectedValueIndicator } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { formatLtv } from '@/helpers/formatters';
import { LoanToValueCellHealthBar } from '../LoanToValueCellHealthBar';
export interface LoanToValueCellHoverContentProps {
  isUnhealthy: boolean;
  ltv: BigIntish | null | undefined;
  lltv: BigIntish | null | undefined;
  marketId: MarketId;
  chainId: ChainId;
  liquidationPrice: bigint | null;
  decimals: number;
  withProtectedLltv?: boolean;
  liquidationPriceUnit: string;
}
const percentageFormatter = formatters.percent.default('-').digits(2).trailingZero(false).unit('%');
const LoanToValueCellHoverContent: React.FC<LoanToValueCellHoverContentProps> = t0 => {
  const $ = _c(48);
  const {
    isUnhealthy,
    ltv,
    lltv,
    marketId,
    chainId,
    liquidationPrice,
    decimals,
    withProtectedLltv: t1,
    liquidationPriceUnit
  } = t0;
  const withProtectedLltv = t1 === undefined ? false : t1;
  const textColorClass = isUnhealthy ? "text-destructive-foreground" : "text-foreground";
  let t2;
  if ($[0] !== chainId || $[1] !== marketId) {
    t2 = <span className="text-2xs text-foreground"><ProtectedLltvLabel defaultLabel="LTV / Liquidation LTV" marketId={marketId} chainId={chainId} /></span>;
    $[0] = chainId;
    $[1] = marketId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== textColorClass) {
    t3 = cn("text-2xs", textColorClass);
    $[3] = textColorClass;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== ltv) {
    t4 = formatLtv(toBigInt(ltv));
    $[5] = ltv;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      display: "flex",
      alignItems: "center",
      lineHeight: "normal",
      gap: "2px"
    };
    t7 = <span>/</span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] !== chainId || $[13] !== lltv || $[14] !== marketId || $[15] !== withProtectedLltv) {
    t8 = withProtectedLltv ? <ProtectedLltv lltv={lltv} marketId={marketId} chainId={chainId} /> : percentageFormatter.of(toBigInt(lltv), 18);
    $[12] = chainId;
    $[13] = lltv;
    $[14] = marketId;
    $[15] = withProtectedLltv;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <span className="text-2xs text-foreground" style={t6}>{t7}{t8}</span>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t9) {
    t10 = <div className="flex items-center gap-[2px]">{t5}{t9}</div>;
    $[19] = t5;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t2) {
    t11 = <div className="py-xs px-sm gap-sm flex flex-row items-center justify-between">{t2}{t10}</div>;
    $[22] = t10;
    $[23] = t2;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-2xs text-foreground shrink-0">Oracle liquidation price</span>;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== textColorClass) {
    t13 = cn("text-2xs flex min-w-0 flex-wrap items-center justify-end gap-[2px] text-right leading-normal", textColorClass);
    $[26] = textColorClass;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== chainId || $[29] !== marketId || $[30] !== withProtectedLltv) {
    t14 = withProtectedLltv && <ProtectedValueIndicator marketId={marketId} chainId={chainId} />;
    $[28] = chainId;
    $[29] = marketId;
    $[30] = withProtectedLltv;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== decimals || $[33] !== liquidationPrice || $[34] !== liquidationPriceUnit) {
    t15 = formatters.short.default("-").digits(getDigitsFromPrice(liquidationPrice, decimals)).trailingZero(false).unit(liquidationPriceUnit).of(liquidationPrice, decimals);
    $[32] = decimals;
    $[33] = liquidationPrice;
    $[34] = liquidationPriceUnit;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t13 || $[37] !== t14 || $[38] !== t15) {
    t16 = <div className="py-xs px-sm gap-sm flex flex-row items-start justify-between">{t12}<div className="flex min-w-0 flex-1 items-center justify-end"><span className={t13}>{t14}{t15}</span></div></div>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== isUnhealthy || $[41] !== lltv || $[42] !== ltv) {
    t17 = <div className="py-xs px-sm gap-sm flex w-[100%] flex-row items-center justify-between"><LoanToValueCellHealthBar ltv={ltv} lltv={lltv} isPositionUnhealthy={isUnhealthy} /></div>;
    $[40] = isUnhealthy;
    $[41] = lltv;
    $[42] = ltv;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t11 || $[45] !== t16 || $[46] !== t17) {
    t18 = <div className="py-sm flex w-full flex-col">{t11}{t16}{t17}</div>;
    $[44] = t11;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};
export default memo(LoanToValueCellHoverContent);